import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../../components/ui/avatar";
import {
  Mail,
  Phone,
  Building2,
  Target,
  Globe,
  MapPin,
  Briefcase,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Users,
  DollarSign,
  Edit3
} from "lucide-react";
import { useConvexAuth } from "../../../services/convexService";
import { toast } from "sonner";
import { useAuth } from "../../../contexts/AuthContext";
import { useLanguage } from "../../../contexts/LanguageContext";
import { Id } from "../../../../backend/_generated/dataModel";
import CurrencyDisplay from "../../../components/currency/CurrencyDisplay";

interface UserDetailProps {
  userId: Id<"users">;
}

const UserDetail = () => {
  const { userId } = useParams<{ userId: string }>();
  const { getUser } = useConvexAuth();
  const { user: currentUser } = useAuth(); // Current logged in user
  const { t, language } = useLanguage();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        if (userId) {
          const userData = await getUser({ userId: userId as Id<"users"> });
          setUser(userData);
        }
      } catch (err: any) {
        setError(err.message || "Failed to load user details");
        toast.error(err.message || "Failed to load user details");
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUser();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle className="text-red-500">Error Loading User</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error || "User not found"}</p>
            <Button onClick={() => window.history.back()}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getUserStatusVariant = (status: string) => {
    switch (status) {
      case "approved":
        return "default";
      case "pending_approval":
        return "secondary";
      case "rejected":
        return "destructive";
      case "suspended":
        return "outline";
      default:
        return "secondary";
    }
  };

  const getUserStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="w-4 h-4" />;
      case "pending_approval":
        return <Clock className="w-4 h-4" />;
      case "rejected":
        return <XCircle className="w-4 h-4" />;
      case "suspended":
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-3xl font-bold">User Details</h1>
          <Button
            variant="outline"
            onClick={() => window.history.back()}
          >
            Go Back
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-2xl font-bold">{user.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={getUserStatusVariant(user.status)} className={user.status === "approved" ? "bg-green-100 text-green-800" : ""}>
                  {getUserStatusIcon(user.status)}
                  <span className="ml-1 capitalize">{user.status.replace("_", " ")}</span>
                </Badge>
                <Badge variant="outline" className="capitalize">
                  {user.role.replace("_", " ")}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{user.email}</p>
                  </div>
                </div>

                {user.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium">{user.phone}</p>
                    </div>
                  </div>
                )}

                {user.company && (
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Company</p>
                      <p className="font-medium">{user.company}</p>
                    </div>
                  </div>
                )}

                {user.location && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium">{user.location}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {user.role === "investor" && (
                  <>
                    {user.investmentCapacity && (
                      <div className="flex items-start gap-3">
                        <DollarSign className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Investment Capacity</p>
                          <p className="font-medium"><CurrencyDisplay amount={user.investmentCapacity} /></p>
                        </div>
                      </div>
                    )}



                    {user.preferredSectors && user.preferredSectors.length > 0 && (
                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Preferred Sectors</p>
                          <p className="font-medium">{user.preferredSectors.join(", ")}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {user.role === "project_holder" && (
                  <>
                    {user.projectName && (
                      <div className="flex items-start gap-3">
                        <Briefcase className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Project Name</p>
                          <p className="font-medium">{user.projectName}</p>
                        </div>
                      </div>
                    )}

                    {user.fundingGoal && (
                      <div className="flex items-start gap-3">
                        <DollarSign className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Funding Goal</p>
                          <p className="font-medium"><CurrencyDisplay amount={user.fundingGoal} /></p>
                        </div>
                      </div>
                    )}

                    {user.sector && (
                      <div className="flex items-start gap-3">
                        <Briefcase className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Sector</p>
                          <p className="font-medium capitalize">{user.sector}</p>
                        </div>
                      </div>
                    )}

                    {/* Project Purpose - Display only, no editing here */}
                    {user.projectPurpose && (
                      <div className="flex items-start gap-3">
                        <Briefcase className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">{t("projectPurpose.title")}</p>
                          <p className="font-medium">
                            {user.projectPurpose === "sell"
                              ? t("projectPurpose.wantToSell")
                              : user.projectPurpose === "getInvestment"
                                ? t("projectPurpose.wantToGetInvestment")
                                : t("common.notSpecified")}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {user.linkedin && (
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">LinkedIn</p>
                      <a
                        href={user.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-500 hover:underline"
                      >
                        {user.linkedin}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {user.bio && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-2">Bio</h3>
                <p className="text-muted-foreground">{user.bio}</p>
              </div>
            )}

            {user.role === "project_holder" && user.projectDescription && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-2">Project Description</h3>
                <p className="text-muted-foreground">{user.projectDescription}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => window.history.back()}>
            Back
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;